'use client';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { useTheme as useAppTheme } from '@/contexts/ThemeContext';
import { useAuth, useSyncBookmarks } from '@/hooks';
import { usePortfolioSummary } from '@/services/query';
import {
  closeModal,
  openModal,
  type RootState,
  setSearchQuery,
  useAppDispatch,
  useUI,
} from '@/store';

export const useMainLayout = () => {
  const { modals } = useUI();
  const dispatch = useAppDispatch();
  const { mode, toggleTheme } = useAppTheme();

  const { isAuthenticated } = useAuth();

  useSyncBookmarks(isAuthenticated);

  const { data: portfolioSummary } = usePortfolioSummary();

  // Modals state from Redux
  const isAuthModalVisible = modals.signIn;
  const isDepositModalVisible = modals.deposit;

  // Local UI state
  const [isMobileSearchVisible, setIsMobileSearchVisible] = useState(false);
  const [isHowItWorksModalVisible, setIsHowItWorksModalVisible] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState(false);
  const [isMobileMenuDrawerOpen, setIsMobileMenuDrawerOpen] = useState(false);

  const searchQuery = useSelector((state: RootState) => state.search.query);

  const notificationsButtonRef = useRef<HTMLButtonElement>(null);
  const notificationsDropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLDivElement>(null);

  const openAuthModal = () => {
    dispatch(openModal('signIn'));
  };

  const closeAuthModal = () => {
    dispatch(closeModal('signIn'));
  };

  const toggleMobileMenuDrawer = () => {
    setIsMobileMenuDrawerOpen((currentState) => !currentState);
  };

  const closeMobileMenuDrawer = () => {
    setIsMobileMenuDrawerOpen(false);
  };

  const toggleMobileSearch = () => {
    setIsMobileSearchVisible((currentState) => !currentState);
  };

  const handleSearchClick = (e: React.MouseEvent | React.FocusEvent) => {
    e.stopPropagation();
    setIsSearchDropdownOpen(true);
  };

  const handleSearchChange = (value: string) => {
    dispatch(setSearchQuery(value));
    if (value && !isSearchDropdownOpen) {
      setIsSearchDropdownOpen(true);
    }
  };

  const handleSearchDropdownClose = () => {
    setIsSearchDropdownOpen(false);
  };

  const openDepositModal = () => {
    dispatch(openModal('deposit'));
  };

  const closeDepositModal = () => {
    dispatch(closeModal('deposit'));
  };

  const toggleNotifications = () => {
    setIsNotificationsOpen((prev) => !prev);
  };

  const closeNotifications = () => {
    setIsNotificationsOpen(false);
  };

  const openHowItWorksModal = () => {
    setIsHowItWorksModalVisible(true);
  };

  const closeHowItWorksModal = () => {
    setIsHowItWorksModalVisible(false);
  };

  // Handle click outside for notifications
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const dropdownEl = notificationsDropdownRef.current;
      const buttonEl = notificationsButtonRef.current;

      if (
        dropdownEl &&
        !dropdownEl.contains(event.target as Node) &&
        buttonEl &&
        !buttonEl.contains(event.target as Node)
      ) {
        closeNotifications();
      }
    };

    if (isNotificationsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isNotificationsOpen, closeNotifications]);

  return {
    state: {
      isAuthModalVisible,
      isDepositModalVisible,
      isMobileSearchVisible,
      isHowItWorksModalVisible,
      isNotificationsOpen,
      isSearchDropdownOpen,
      isMobileMenuDrawerOpen,
      searchQuery,
      isAuthenticated,
      portfolioSummary,
      themeMode: mode,
    },
    handlers: {
      toggleTheme,
      openAuthModal,
      closeAuthModal,
      toggleMobileMenuDrawer,
      closeMobileMenuDrawer,
      toggleMobileSearch,
      handleSearchClick,
      handleSearchChange,
      handleSearchDropdownClose,
      openDepositModal,
      closeDepositModal,
      toggleNotifications,
      closeNotifications,
      openHowItWorksModal,
      closeHowItWorksModal,
    },
    refs: {
      notificationsButtonRef,
      notificationsDropdownRef,
      searchInputRef,
    },
  };
};
